import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AxiosResponse } from "axios";
import apiDefinitions from "../../../../core/api/apiDefinitions";
import { checkPermission } from "../../../../core/permissons/CheckPermissons";
import {
  showError,
  showSuccess,
} from "../../../../core/alert/sweetAlertConfig";
import { all_routes } from "../../../router/all_routes";
import CollapseHeader from "../../../../core/common/collapse-header";

/** DTOs */
interface LocationDTO {
  id: number;
  name: string;
  address: string;
  lat: number;
  lng: number;
  description?: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}
interface BuildingDTO {
  id: number;
  name: string;
  noOfFloors: number;
  locationName: string;
  locationId: number;
  description?: string | null;
}
interface FloorDTO {
  id: number;
  name: string;
  floorNumber: number;
  buildingId: number;
  buildingName: string;
  locationName: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}
interface RoomDTO {
  id: number;
  name: string;
  floorId: number;
  buildingId: number;
}
interface KeyDTO {
  id: string;
  keyCode: string;
  buildingId: number;
  buildingName: string | null;
  floorId: number;
  floorName: string | null;
  roomId: number | null;
  roomName: string | null;
  doorId: string;
  noOfKeys: number;
  vehicleId: string | null;
}
interface GetLocationsResponse {
  locations: LocationDTO[];
  total: number;
}
interface GetAvailableKeysResponse {
  keys: KeyDTO[];
  total: number;
}
interface UserDTO {
  empNumber: string;
  name: string;
  createdAt: string;
  status: string;
  email: string;
  phone: string;
  userType: string;
  departmentNames: string[];
  licenseNumber: string;
  licenseType: string;
  expireDate: string;
}

const navItems = [
  {
    label: "Key Allocations",
    icon: "ti ti-settings",
    link: all_routes.keyAllocations,
    permission: "ROLE_GET_KEY_ALLOCATIONS",
  },
  {
    label: "Add Keys",
    icon: "ti ti-settings",
    link: all_routes.keys,
    permission: "ROLE_ADD_LOCATION",
  },
  {
    label: "Add Vehicle Keys",
    icon: "ti ti-car",
    link: all_routes.vehiclekey,
    permission: "ROLE_ADD_KEY",
  },
];

/** Name with numeric-first sort */
const numericFirstSort = (a: string, b: string) => {
  const na = parseInt(a.replace(/\D+/g, ""), 10);
  const nb = parseInt(b.replace(/\D+/g, ""), 10);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && na !== nb) return na - nb;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
};

const KeyAllocation: React.FC = () => {
  const locationHook = useLocation();
  const navigate = useNavigate();

  const [locations, setLocations] = useState<LocationDTO[]>([]);
  const [buildings, setBuildings] = useState<BuildingDTO[]>([]);
  const [floors, setFloors] = useState<FloorDTO[]>([]);
  const [rooms, setRooms] = useState<RoomDTO[]>([]); // 🆕 rooms loaded by floor

  const [availableKeys, setAvailableKeys] = useState<KeyDTO[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<KeyDTO[]>([]);
  const [users, setUsers] = useState<UserDTO[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [handoverType, setHandoverType] = useState<string>("PERMANENT");
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null
  );
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(
    null
  );
  const [selectedFloorId, setSelectedFloorId] = useState<number | null>(null);
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);
  const [filterType, setFilterType] = useState<string>("BUILDING");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkToken = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      showError("Authentication token not found. Please log in.");
      navigate("/login");
      return false;
    }
    return token;
  };

  /** Initial: locations */
  useEffect(() => {
    const run = async () => {
      if (!checkToken()) return;
      if (!checkPermission("ROLE_GET_LOCATIONS")) {
        showError("You do not have permission to view locations.");
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const payload = { filterType: "", filterValue: "", date: "" };
        const res: AxiosResponse = await apiDefinitions.getLocations(
          "1",
          payload
        );
        if (res.status === 200) {
          const data: GetLocationsResponse = res.data.data;
          setLocations(data.locations || []);
          if ((data.locations || []).length === 0)
            setError("No locations found. Please add a location first.");
        } else {
          showError(res.data?.statusDescription || "Failed to fetch locations");
        }
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.removeItem("token");
          navigate("/login");
        } else showError("Failed to fetch locations. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [navigate]);

  /** Fetch users */
  useEffect(() => {
    const run = async () => {
      if (!checkToken()) return;
      if (!checkPermission("ROLE_GET_USERS")) {
        showError("You do not have permission to view users.");
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        let page = 1;
        let hasMore = true;
        let allUsers: UserDTO[] = [];
        const payload = { filterType: "", filterValue: "", date: "" };
        while (hasMore) {
          const response = await apiDefinitions.getUsers(page, payload);
          if (response.data.status === "S1000") {
            const usersPage = response.data.data.users;
            if (!usersPage || usersPage.length === 0) {
              hasMore = false;
              break;
            }
            const mappedUsers = usersPage
              .filter((user: any) => user.empNumber)
              .map((user: any) => ({
                empNumber: user.empNumber.toString(),
                name: user.name || "Unknown",
                createdAt: user.createdAt
                  ? new Date(user.createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "N/A",
                status: user.status || "PENDING",
                email: user.email || "",
                phone: user.phone || "",
                userType: user.group?.replace("_K", "") || "USER",
                departmentNames: user.departmentAdminSpecificData
                  ?.departmentNames?.length
                  ? [user.departmentAdminSpecificData.departmentNames[0]]
                  : [],
                licenseNumber: user.driverSpecificData?.licenseNumber || "",
                licenseType: user.driverSpecificData?.licenseType || "",
                expireDate: user.driverSpecificData?.expireDate
                  ? new Date(user.driverSpecificData.expireDate)
                      .toISOString()
                      .split("T")[0]
                  : "",
              }));
            allUsers.push(...mappedUsers);
            page++;
          } else {
            showError(
              "Failed to fetch users: " + response.data.statusDescription
            );
            break;
          }
        }
        setUsers(allUsers);
        if (allUsers.length === 0) setError("No users found.");
      } catch (e: any) {
        showError("Failed to fetch users. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [navigate]);

  /** When location changes → buildings & reset */
  useEffect(() => {
    const run = async () => {
      if (!selectedLocationId) {
        setBuildings([]);
        setSelectedBuildingId(null);
        setFloors([]);
        setRooms([]);
        setAvailableKeys([]);
        setSelectedFloorId(null);
        setSelectedRoomId(null);
        setSelectedKeys([]);
        setFilterType("BUILDING");
        return;
      }
      if (!checkToken()) return;
      if (!checkPermission("ROLE_GET_LOCATIONS")) {
        showError("You do not have permission to view buildings.");
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const res: AxiosResponse =
          await apiDefinitions.getBuildingsByLocationId(selectedLocationId);
        if (res.status === 200) {
          const data: BuildingDTO[] = res.data.data;
          setBuildings(data || []);
          setSelectedBuildingId(null);
          setFloors([]);
          setRooms([]);
          setAvailableKeys([]);
          setSelectedFloorId(null);
          setSelectedRoomId(null);
          setSelectedKeys([]);
          setFilterType("BUILDING");
          if ((data || []).length === 0)
            setError(
              `No buildings found for location ID ${selectedLocationId}.`
            );
        } else {
          showError(res.data?.statusDescription || "Failed to fetch buildings");
        }
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.removeItem("token");
          navigate("/login");
        } else showError("Failed to fetch buildings. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [selectedLocationId, navigate]);

  /**
   * When building changes → fetch floors + available keys FOR BUILDING
   */
  useEffect(() => {
    const run = async () => {
      if (!selectedBuildingId) {
        setFloors([]);
        setRooms([]);
        setAvailableKeys([]);
        setSelectedFloorId(null);
        setSelectedRoomId(null);
        setSelectedKeys([]);
        setFilterType("BUILDING");
        return;
      }
      if (!checkToken()) return;

      setIsLoading(true);
      setError(null);
      try {
        // Floors
        let floorsRes: AxiosResponse | null = null;
        if (checkPermission("ROLE_VIEW_FLOOR")) {
          floorsRes = await apiDefinitions.getFloorsByBuildingId(
            selectedBuildingId
          );
        }

        if (floorsRes && floorsRes.status === 200) {
          const fData: FloorDTO[] = floorsRes.data.data || [];
          const sortedFloors = fData.sort((a, b) => {
            if (a.floorNumber === 0) return -1;
            if (b.floorNumber === 0) return 1;
            return a.floorNumber - b.floorNumber;
          });
          setFloors(sortedFloors);
          setRooms([]);
          setSelectedFloorId(null);
          setSelectedRoomId(null);
          if (sortedFloors.length === 0) {
            setError(`No floors found for building ID ${selectedBuildingId}.`);
          }
        }

        // Keys for building
        if (!checkPermission("ROLE_GET_KEYS")) {
          showError("You do not have permission to view keys.");
          setIsLoading(false);
          return;
        }
        const keysRes: AxiosResponse = await apiDefinitions.getAvailableKeys(1, {
          filterType: "BUILDING",
          filterValue: String(selectedBuildingId),
        });

        if (keysRes.status === 200) {
          const kData: GetAvailableKeysResponse = keysRes.data.data;
          const sortedKeys = (kData.keys || [])
            .slice()
            .sort((a, b) => numericFirstSort(a.keyCode, b.keyCode));
          setAvailableKeys(sortedKeys);
        } else {
          showError(
            keysRes.data?.statusDescription || "Failed to fetch available keys"
          );
        }
      } catch (e: any) {
        if (
          e.response?.status === 401 &&
          e.response?.data?.status !== "E1001"
        ) {
          localStorage.removeItem("token");
          navigate("/login");
        } else {
          setError(
            e.response?.data?.statusDescription ||
              "Failed to fetch floors/keys. Displaying available keys."
          );
          if (
            e.response?.config?.url?.includes("key-allocation/available-keys")
          ) {
            setAvailableKeys([]);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [selectedBuildingId, navigate]);

  /**
   * When FLOOR changes → fetch keys FOR THAT FLOOR + rooms for that floor
   * 👉 Fixes: room dropdown was empty after selecting floor
   */
  useEffect(() => {
    const run = async () => {
      if (!selectedBuildingId || !selectedFloorId) {
        setRooms([]);
        setSelectedRoomId(null);
        return;
      }
      if (!checkToken()) return;

      setIsLoading(true);
      setError(null);
      try {
        // 1) Fetch keys for floor
        if (!checkPermission("ROLE_GET_KEYS")) {
          showError("You do not have permission to view keys.");
          setIsLoading(false);
          return;
        }
        const keysRes: AxiosResponse = await apiDefinitions.getAvailableKeys(1, {
          filterType: "FLOOR",
          filterValue: String(selectedFloorId),
        });

        if (keysRes.status === 200) {
          const kData: GetAvailableKeysResponse = keysRes.data.data;
          const sortedKeys = (kData.keys || [])
            .slice()
            .sort((a, b) => numericFirstSort(a.keyCode, b.keyCode));
          setAvailableKeys(sortedKeys);
        } else {
          showError(
            keysRes.data?.statusDescription || "Failed to fetch available keys"
          );
          setAvailableKeys([]);
        }

        // 2) Fetch rooms for selected floor
        if (!checkPermission("ROLE_GET_ROOMS")) {
          // No room permission – derive rooms from keys as fallback
          const roomMap = new Map<number, RoomDTO>();
          (keysRes?.data?.data?.keys || []).forEach((k: KeyDTO) => {
            if (k.roomId && k.roomName) {
              roomMap.set(k.roomId, {
                id: k.roomId,
                name: k.roomName,
                floorId: selectedFloorId,
                buildingId: selectedBuildingId,
              });
            }
          });
          setRooms(
            Array.from(roomMap.values()).sort((a, b) =>
              numericFirstSort(a.name, b.name)
            )
          );
          return;
        }

        const roomsRes: AxiosResponse = await apiDefinitions.getRoomsByFloorId(
          selectedFloorId
        );
        if (roomsRes.status === 200 && Array.isArray(roomsRes.data.data)) {
          const rData: RoomDTO[] = roomsRes.data.data;
          const sortedRooms = rData
            .slice()
            .sort((a, b) => numericFirstSort(a.name, b.name));
          setRooms(sortedRooms);
        } else {
          setRooms([]);
          setError(
            roomsRes.data?.statusDescription ||
              "Failed to fetch rooms for selected floor."
          );
        }
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.removeItem("token");
          navigate("/login");
        } else {
          setRooms([]);
          setError(
            e.response?.data?.statusDescription ||
              "Failed to fetch rooms/keys for selected floor."
          );
        }
      } finally {
        setIsLoading(false);
      }
    };

    run();
  }, [selectedBuildingId, selectedFloorId, navigate]);

  const selectedLocationName = useMemo(
    () =>
      selectedLocationId
        ? locations.find((l) => l.id === selectedLocationId)?.name || ""
        : "",
    [selectedLocationId, locations]
  );
  const selectedBuildingName = useMemo(
    () =>
      selectedBuildingId
        ? buildings.find((b) => b.id === selectedBuildingId)?.name || ""
        : "",
    [selectedBuildingId, buildings]
  );

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    const lowerSearch = searchTerm.toLowerCase();
    return users.filter(
      (user) =>
        user.empNumber.toLowerCase().includes(lowerSearch) ||
        user.name.toLowerCase().includes(lowerSearch)
    );
  }, [users, searchTerm]);

  const toggleKeySelection = (key: KeyDTO) => {
    setSelectedKeys((prev) =>
      prev.some((k) => k.id === key.id)
        ? prev.filter((k) => k.id !== key.id)
        : [...prev, key]
    );
  };

  const removeFromCart = (keyId: string) => {
    setSelectedKeys((prev) => prev.filter((k) => k.id !== keyId));
  };

  const handleHandover = async () => {
    if (!checkToken()) return;
    if (!checkPermission("ROLE_MARK_HANDOVER")) {
      showError("You do not have permission to mark key handover.");
      return;
    }
    if (!selectedUser) {
      showError("Please select an employee to handover to.");
      return;
    }
    if (selectedKeys.length === 0) {
      showError("Please select at least one key to handover.");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const payload = {
        keyIds: selectedKeys.map((key) => Number(key.id)),
        handoverTo: selectedUser,
        handoverType: handoverType,
      };
      const res: AxiosResponse = await apiDefinitions.markHandover(payload);
      if (res.status === 200) {
        showSuccess("Handover marked successfully");
        setSelectedKeys([]);
        setSelectedUser("");
        setHandoverType("PERMANENT");
        setSearchTerm("");
        const filterValue =
          filterType === "FLOOR" && selectedFloorId
            ? String(selectedFloorId)
            : String(selectedBuildingId);
        const keysRes: AxiosResponse = await apiDefinitions.getAvailableKeys(
          1,
          { filterType, filterValue }
        );
        if (keysRes.status === 200) {
          const kData: GetAvailableKeysResponse = keysRes.data.data;
          const sortedKeys = (kData.keys || [])
            .slice()
            .sort((a, b) => numericFirstSort(a.keyCode, b.keyCode));
          setAvailableKeys(sortedKeys);
        }
      } else {
        showError(res.data?.statusDescription || "Failed to mark handover.");
      }
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      } else {
        showError(
          e.response?.data?.statusDescription ||
            "Failed to mark handover. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const KeyCard: React.FC<{ keyItem: KeyDTO }> = ({ keyItem }) => (
    <div className="card h-100 border border-2 shadow-sm transition-all hover:shadow-md">
      <div className="card-body p-2 d-flex align-items-center">
        <div className="form-check mb-0 me-2">
          <input
            type="checkbox"
            className="form-check-input"
            checked={selectedKeys.some((k) => k.id === keyItem.id)}
            onChange={() => toggleKeySelection(keyItem)}
          />
        </div>
        <div className="flex-grow-1">
          <span className="fw-semibold text-dark">{keyItem.keyCode}</span>
          <small className="d-block text-muted">
            ({keyItem.noOfKeys} keys)
          </small>
        </div>
        <small className="text-muted text-end">
          {keyItem.roomName || "No Room"}
        </small>
      </div>
    </div>
  );

  return (
    <div className="page-wrapper">
      <style>{`
        .transition-all {
          transition: all 0.2s ease-in-out;
        }
        .hover\\:shadow-md:hover {
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        .form-select, .form-control {
          border-radius: 0.375rem;
          box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.075);
        }
        .dropdown-menu {
          max-height: 200px;
          overflow-y: auto;
          border-radius: 0.375rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        .dropdown-item:hover {
          background-color: #f8f9fa;
        }
        .card {
          border-radius: 0.5rem;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }
        .btn-primary {
          background-color: #007bff;
          border-color: #007bff;
          transition: background-color 0.2s;
        }
        .btn-primary:hover {
          background-color: #0056b3;
          border-color: #004085;
        }
        .btn-danger {
          transition: background-color 0.2s;
        }
        .btn-danger:hover {
          background-color: #c82333;
          border-color: #bd2130;
        }
        .table th, .table td {
          vertical-align: middle;
        }
      `}</style>

      <div className="content container-fluid py-4">
        <div className="row">
          <div className="col-12">
            <div className="page-header mb-4">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">
                    Key Management/Key Dashboard/Key Allocations
                  </h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body pb-0 pt-2">
                <ul
                  className="nav nav-tabs nav-tabs-bottom"
                  style={{ gap: "12px", display: "flex" }}
                >
                  {navItems.map((item, i) =>
                    item.permission && checkPermission(item.permission) ? (
                      <li className="nav-item me-item" key={i}>
                        <Link
                          to={item.link}
                          className={`nav-link px-0 ${
                            locationHook.pathname === item.link ? "active" : ""
                          }`}
                        >
                          <i className={item.icon}></i> {item.label}
                        </Link>
                      </li>
                    ) : null
                  )}
                </ul>
              </div>
            </div>

            <div className="card">
              <div className="card-body p-4">
                <div className="row g-3 mb-4">
                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Location</label>
                    <select
                      className="form-select"
                      value={selectedLocationId || ""}
                      onChange={(e) =>
                        setSelectedLocationId(
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                      disabled={isLoading}
                    >
                      <option value="">
                        {locations.length === 0
                          ? "No locations available"
                          : "Select a location"}
                      </option>
                      {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Building</label>
                    <select
                      className="form-select"
                      value={selectedBuildingId || ""}
                      onChange={(e) =>
                        setSelectedBuildingId(
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                      disabled={isLoading || !selectedLocationId}
                    >
                      <option value="">
                        {buildings.length === 0
                          ? "No buildings available"
                          : "Select a building"}
                      </option>
                      {buildings.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Floor</label>
                    <select
                      className="form-select"
                      value={selectedFloorId || ""}
                      onChange={(e) => {
                        const floorId = e.target.value
                          ? Number(e.target.value)
                          : null;
                        setSelectedFloorId(floorId);
                        setFilterType(floorId ? "FLOOR" : "BUILDING");
                        setSelectedRoomId(null);
                      }}
                      disabled={
                        isLoading || !selectedBuildingId || floors.length === 0
                      }
                    >
                      <option value="">
                        {floors.length === 0
                          ? "No floors available"
                          : "Select a floor"}
                      </option>
                      {floors.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Room</label>
                    <select
                      className="form-select"
                      value={selectedRoomId || ""}
                      onChange={(e) =>
                        setSelectedRoomId(
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                      disabled={
                        isLoading ||
                        !selectedBuildingId ||
                        !selectedFloorId || // require floor selected
                        rooms.length === 0
                      }
                    >
                      <option value="">
                        {rooms.length === 0
                          ? "No rooms available"
                          : "Select a room"}
                      </option>
                      {rooms.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {isLoading && (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}

                {error && (
                  <div
                    className="alert alert-danger alert-dismissible fade show"
                    role="alert"
                  >
                    {error}
                    <button
                      type="button"
                      className="btn-close"
                      data-bs-dismiss="alert"
                      aria-label="Close"
                    ></button>
                  </div>
                )}

                {selectedLocationId &&
                  selectedBuildingId &&
                  availableKeys.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-center mb-4 fw-semibold">
                        {selectedLocationName} - 🏢 {selectedBuildingName}
                        {selectedFloorId &&
                          ` - Floor: ${
                            floors.find((f) => f.id === selectedFloorId)
                              ?.name || ""
                          }`}
                        {selectedRoomId &&
                          ` - Room: ${
                            rooms.find((r) => r.id === selectedRoomId)?.name ||
                            ""
                          }`}
                      </h4>

                      <div className="container-fluid">
                        <h5 className="mb-3 fw-semibold">Available Keys</h5>
                        <div className="row g-3">
                          {availableKeys
                            .filter(
                              (key) =>
                                !selectedRoomId || key.roomId === selectedRoomId
                            )
                            .map((key) => (
                              <div
                                key={key.id}
                                className="col-6 col-sm-4 col-md-3"
                              >
                                <KeyCard keyItem={key} />
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}

                {/* Handover section */}
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">
                      Handover To
                    </label>
                    <div className="dropdown">
                      <input
                        type="text"
                        className="form-control"
                        placeholder={
                          users.length === 0
                            ? "No users available"
                            : "Search by name or emp ID"
                        }
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        disabled={isLoading || users.length === 0}
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      />
                      <ul className="dropdown-menu w-100">
                        {filteredUsers.length === 0 ? (
                          <li>
                            <span className="dropdown-item text-muted">
                              No matching users
                            </span>
                          </li>
                        ) : (
                          filteredUsers.map((user) => (
                            <li key={user.empNumber}>
                              <button
                                className="dropdown-item"
                                onClick={() => {
                                  setSelectedUser(user.empNumber);
                                  setSearchTerm(
                                    `${user.name} (${user.empNumber})`
                                  );
                                }}
                              >
                                {user.name} ({user.empNumber})
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">
                      Handover Type
                    </label>
                    <select
                      className="form-select"
                      value={handoverType}
                      onChange={(e) => setHandoverType(e.target.value)}
                      disabled={isLoading}
                    >
                      <option value="PERMANENT">Permanent</option>
                      <option value="TEMPORARY">Temporary</option>
                    </select>
                  </div>
                  <div className="col-md-4 d-flex align-items-end">
                    <button
                      className="btn btn-success w-100"
                      onClick={handleHandover}
                      disabled={isLoading || !selectedUser}
                    >
                      {isLoading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-check me-2"></i> Handover Keys
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {selectedKeys.length > 0 && (
                  <div className="card mt-4">
                    <div className="card-header bg-light">
                      <h5 className="card-title mb-0">
                        Selected Keys ({selectedKeys.length})
                      </h5>
                    </div>
                    <div className="card-body p-4">
                      <div className="table-responsive mb-4">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>Key Code</th>
                              <th>Number of Keys</th>
                              <th>Room</th>
                              <th>Building</th>
                              <th>Floor</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedKeys.map((key) => (
                              <tr key={key.id}>
                                <td>{key.keyCode}</td>
                                <td>{key.noOfKeys}</td>
                                <td>{key.roomName || "No Room"}</td>
                                <td>{key.buildingName || "N/A"}</td>
                                <td>{key.floorName || "N/A"}</td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => removeFromCart(key.id)}
                                  >
                                    <i className="ti ti-trash me-1"></i> Remove
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyAllocation;
